import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Public site components
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import OverviewSection from "./components/OverviewSection";
import VisionSection from "./components/VisionSection";
import ArchitectureSection from "./components/ArchitectureSection";
import PlanSection from "./components/PlanSection";
import RevenueSection from "./components/RevenueSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";

// Admin components
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Public site component
const PublicSite = () => {
  const [siteContent, setSiteContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSiteContent();
    
    // Reload content every 30 seconds to catch admin updates
    const interval = setInterval(loadSiteContent, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSiteContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setSiteContent(response.data);
      console.log('✅ Content loaded from database:', response.data);
    } catch (error) {
      console.error('Failed to load site content:', error);
      // Fallback to config file if API fails
      import('./config/content').then(module => {
        setSiteContent(module.siteConfig);
        console.log('⚠️ Fallback: Content loaded from config file');
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!siteContent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Failed to load site content</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header siteContent={siteContent} />
      <main>
        <HeroSection content={siteContent.hero} />
        <OverviewSection content={siteContent.overview} />
        <VisionSection content={siteContent.vision} />
        <ArchitectureSection content={siteContent.architecture} />
        <PlanSection content={siteContent.plan} architecture={siteContent.architecture} />
        <RevenueSection content={siteContent.revenueModel} />
        <ContactSection content={siteContent.contact} />
        <Footer content={siteContent} />
      </main>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicSite />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
